import { useState, useEffect, useRef } from "react";
import { supabase, BRAND, BRAND_LIGHT, MUTED, FAINT, text, btn, linkBtn, errorText, segWrap, segBtn, LINE, WHITE, INK, ROW_LINE, SURFACE, TEXT, BG, DANGER } from "./constants";
import { CandidateAvatar, CloseButton, ConfirmBar, evalCriteria, evalScorePct } from "./shared";
import SimpleGuide, { GuideLoader } from "./SimpleGuide";
import { useIsMobile } from "./lib/responsive";
import { useLocalized } from "./lib/translate";

// ─── MODO ENTREVISTA (21/10) ──────────────────────────────────────────────────
// Pantalla completa para el momento de la entrevista: un candidato y un
// entrevistador, sin la tabla ni las notas de otros (distraen y sesgan).
// Por cada vector, las preguntas de SU guía (qué preguntar y qué escuchar) a la
// izquierda, y a la derecha la puntuación 1-2-3 con un cuadro de notas debajo.
//
// Guarda EXACTAMENTE lo mismo que el formulario de la pestaña Evaluación, con
// su misma función `persist` (eval_managers / eval_scores / eval_notes): al
// cerrar, la entrevista sale en la tabla como cualquier otra. Las notas de cada
// vector se juntan con el comentario general en eval_notes[entrevistador].
// Se guarda solo a los pocos segundos de cada cambio, para no perder nada si
// se cierra la pestaña. La guía es la de SimpleGuide; si el candidato aún no
// tiene, SimpleGuide la prepara (no se copia su lógica).

const parseStored = (v) => {
  if (!v) return {};
  if (typeof v === "object") return v;
  try { return JSON.parse(v) || {}; } catch (e) { return {}; }
};

export default function InterviewMode({ position, candidate: fixedCandidate = null, candidates = [], lang: appLang, photoUrls = {}, initialName = "", persist, onClose }) {
  // Idioma de la entrevista: se elige al empezar (por defecto, el de la web) y
  // manda en toda la pantalla, en la guía y en los nombres de los vectores.
  const [lang, setLang] = useState(appLang === "es" ? "es" : "en");
  const es = lang === "es";
  const isMobile = useIsMobile();
  const categories = evalCriteria(lang, position.evalQuestions);
  const ids = categories.flatMap(c => c.items.map(it => it.id));

  // En la ficha llega el candidato; en la posición se elige al empezar
  const [candidate, setCandidate] = useState(fixedCandidate);
  const [stored, setStored] = useState(() => parseStored(fixedCandidate?.interviewerGuide));
  const [guideLoaded, setGuideLoaded] = useState(false);
  const [name, setName] = useState(initialName);
  const [started, setStarted] = useState(false);
  const managerId = useRef("m" + Date.now()).current;
  const [scores, setScores] = useState({});
  const [secNotes, setSecNotes] = useState({});  // { c1: "lo que dijo" }
  const [general, setGeneral] = useState("");
  const [saveState, setSaveState] = useState(null); // null | "saving" | "saved" | error text
  const [askClose, setAskClose] = useState(false);
  const [showOpening, setShowOpening] = useState(true);
  const dirty = useRef(false);

  // La guía se lee de la base al abrir (la copia en memoria puede ser vieja)
  useEffect(() => {
    if (!candidate) return undefined;
    let alive = true;
    setGuideLoaded(false);
    setStored(parseStored(candidate.interviewerGuide));
    supabase.from("candidates").select("interviewer_guide").eq("id", candidate.id).maybeSingle().then(({ data }) => {
      if (!alive) return;
      const db = parseStored(data?.interviewer_guide);
      if (Object.keys(db).length) setStored(db);
      setGuideLoaded(true);
    });
    return () => { alive = false; };
    // eslint-disable-next-line react-hooks/exhaustive-deps
  }, [candidate?.id]);
  // Solo la guía del idioma de la pantalla: con la del otro, la entrevista sale
  // mitad en cada idioma. Si falta, SimpleGuide la genera (sameLangOnly).
  const guideKey = `simple_${es ? "es" : "en"}`;
  const guide = stored[guideKey] || null;
  const hasGuide = !!guide?.sections;
  const bySection = Object.fromEntries((guide?.sections || []).map(s => [s.id, s.questions || []]));

  // Texto de comentarios: el general y, debajo, lo apuntado en cada vector
  const composeNotes = () => {
    const blocks = categories
      .map(cat => [cat.name, (secNotes[cat.id] || "").trim()])
      .filter(([, n]) => n)
      .map(([nm, n]) => `${nm}: ${n}`);
    // Una línea por bloque, sin línea en blanco entre ellos (Borja, 23/10)
    return [general.trim(), ...blocks].filter(Boolean).join("\n");
  };
  const scored = ids.filter(k => scores[k]).length;
  const hasContent = scored > 0 || general.trim() || Object.values(secNotes).some(v => v.trim());

  const save = async () => {
    if (!name.trim() || !hasContent) return true;
    setSaveState("saving");
    try {
      const nm = name.trim();
      await persist(candidate, ({ managers, scores: all, notes }) => ({
        managers: managers.some(m => m.id === managerId) ? managers.map(m => (m.id === managerId ? { ...m, name: nm } : m)) : [...managers, { id: managerId, name: nm, role: "" }],
        scores: { ...all, [managerId]: Object.fromEntries(Object.entries(scores).filter(([k, v]) => v > 0 && ids.includes(k))) },
        notes: { ...notes, [managerId]: composeNotes() },
      }));
      dirty.current = false;
      setSaveState("saved");
      return true;
    } catch (e) {
      setSaveState((es ? "No se guardó: " : "Not saved: ") + e.message);
      return false;
    }
  };

  // Guardado automático a los 3 s del último cambio
  useEffect(() => {
    if (!started) return undefined;
    dirty.current = true;
    const tmo = setTimeout(() => { save(); }, 3000);
    return () => clearTimeout(tmo);
    // eslint-disable-next-line react-hooks/exhaustive-deps
  }, [scores, secNotes, general, started]);

  const finish = async () => { if (await save()) onClose(true); };
  const tryClose = () => { if (started && hasContent) setAskClose(true); else onClose(false); };
  useEffect(() => {
    const onKey = (e) => { if (e.key === "Escape" && !askClose) tryClose(); };
    window.addEventListener("keydown", onKey);
    return () => window.removeEventListener("keydown", onKey);
    // eslint-disable-next-line react-hooks/exhaustive-deps
  }, [askClose, started, hasContent]);

  const pct = evalScorePct(scores, ids);
  const pad = isMobile ? 16 : 24;
  const card = { background: WHITE, border: `1.5px solid ${LINE}`, borderRadius: 14 };
  const field = { width: "100%", boxSizing: "border-box", border: `1.5px solid ${LINE}`, borderRadius: 8, fontSize: isMobile ? 16 : 13, fontFamily: "inherit", color: INK, background: WHITE, outline: "none", padding: "8px 12px" };
  const focus = { onFocus: e => { e.target.style.borderColor = BRAND; }, onBlur: e => { e.target.style.borderColor = LINE; } };
  const photoUrl = candidate ? photoUrls[candidate.id] : null;
  const avatar = !candidate ? null : photoUrl
    ? <img src={photoUrl} alt={candidate.name} style={{ width: 40, height: 40, borderRadius: "50%", objectFit: "cover", border: `1.5px solid ${LINE}`, flexShrink: 0 }} />
    : <CandidateAvatar name={candidate.name} size={40} />;
  const canStart = !!candidate && !!name.trim();
  const saveTxt = saveState === "saving" ? (es ? "Guardando…" : "Saving…")
    : saveState === "saved" ? (es ? "✓ Guardado" : "✓ Saved")
    : saveState ? saveState : (started ? (es ? "Se guarda solo" : "Saves automatically") : "");

  // ── Antes de empezar: a quién (si se abre desde la posición) y quién entrevista ──
  const fieldLabel = { display: "block", fontSize: 11, fontWeight: 700, color: MUTED, textTransform: "uppercase", letterSpacing: 0.4, marginBottom: 6 };
  const startCard = (
    <div style={{ ...card, padding: pad, maxWidth: 520, margin: isMobile ? "16px auto" : "48px auto" }}>
      <div style={{ ...text.cardTitle, marginBottom: 6 }}>{es ? "Entrevista a pantalla completa" : "Full-screen interview"}</div>
      <p style={{ ...text.body, margin: "0 0 16px" }}>{es ? "Las preguntas de la guía y la puntuación juntas, vector a vector. Se guarda sola y sale en la tabla de Evaluación como cualquier otra entrevista." : "The guide's questions and the scoring together, vector by vector. It saves itself and shows up in the Evaluation table like any other interview."}</p>
      {candidates.length > 0 && <>
        <label style={fieldLabel}>{es ? "Candidato" : "Candidate"}</label>
        <select value={candidate ? String(candidate.id) : ""} onChange={e => setCandidate(candidates.find(c => String(c.id) === e.target.value) || null)}
          style={{ ...field, height: isMobile ? 44 : 40, marginBottom: 14, cursor: "pointer", color: candidate ? INK : MUTED }}>
          <option value="">{es ? "Elige un candidato..." : "Choose a candidate..."}</option>
          {candidates.map(c => <option key={c.id} value={c.id}>{c.name}</option>)}
        </select>
      </>}
      <label style={fieldLabel}>{es ? "Entrevistador" : "Interviewer"}</label>
      <input autoFocus={!!candidate} value={name} onChange={e => setName(e.target.value)} onKeyDown={e => { if (e.key === "Enter" && canStart) setStarted(true); }}
        placeholder={es ? "Nombre o iniciales" : "Name or initials"} style={{ ...field, height: isMobile ? 44 : 40, marginBottom: 14 }} {...focus} />
      {/* Quién (candidato y entrevistador) primero; el idioma es un ajuste */}
      <label style={fieldLabel}>{es ? "Idioma de la entrevista" : "Interview language"}</label>
      <div style={segWrap} role="group" aria-label={es ? "Idioma de la entrevista" : "Interview language"}>
        {[["en", "English"], ["es", "Español"]].map(([v, l]) => (
          <button key={v} type="button" onClick={() => setLang(v)} aria-pressed={lang === v}
            style={{ ...segBtn(lang === v), flex: 1, ...(isMobile ? { minHeight: 40 } : null) }}>{l}</button>
        ))}
      </div>
      <button onClick={() => setStarted(true)} disabled={!canStart}
        style={{ ...(canStart ? btn.primary : btn.disabled), width: "100%", marginTop: 14, height: isMobile ? 48 : 42 }}>
        {es ? "Empezar entrevista" : "Start interview"}
      </button>
    </div>
  );

  // ── Un vector: preguntas de la guía + puntuación ──
  const vectorCard = (cat) => {
    const qs = bySection[cat.id] || [];
    const done = cat.items.filter(it => scores[it.id]).length;
    return (
      <div key={cat.id} style={{ ...card, overflow: "hidden" }}>
        <div style={{ display: "flex", alignItems: "center", justifyContent: "space-between", gap: 10, padding: `14px ${pad}px`, borderBottom: `1px solid ${ROW_LINE}` }}>
          {/* Título de sección en negro: el azul es solo para lo pulsable (DESIGN_SYSTEM) */}
          <span style={{ fontSize: 12, fontWeight: 800, color: INK, textTransform: "uppercase", letterSpacing: 0.6 }}>{cat.name}</span>
          <span style={{ fontSize: 12, color: MUTED }}>{done}/{cat.items.length} {es ? "puntuadas" : "scored"}</span>
        </div>
        <div style={{ display: "grid", gridTemplateColumns: isMobile ? "minmax(0, 1fr)" : "minmax(0, 1.6fr) minmax(260px, 1fr)", gap: 0 }}>
          {/* Qué preguntar */}
          <div style={{ padding: `12px ${pad}px 16px`, borderRight: isMobile ? "none" : `1px solid ${ROW_LINE}` }}>
            <div style={{ fontSize: 11, fontWeight: 700, color: MUTED, textTransform: "uppercase", letterSpacing: 0.4, marginBottom: 8 }}>{es ? "Qué preguntar" : "What to ask"}</div>
            {qs.length === 0 && <div style={{ ...text.body, color: MUTED }}>{hasGuide ? (es ? "La guía no trae preguntas para este vector." : "The guide has no questions for this vector.") : (es ? "Sin guía todavía." : "No guide yet.")}</div>}
            {qs.map((q, i) => (
              <div key={i} style={{ marginBottom: i === qs.length - 1 ? 0 : 14 }}>
                <div style={{ fontSize: 13, fontWeight: 700, color: INK, lineHeight: 1.45 }}>{q.ask}</div>
                {q.listen && <div style={{ fontSize: 12, color: MUTED, lineHeight: 1.45, marginTop: 2 }}>{es ? "Escucha: " : "Listen for: "}{q.listen}</div>}
              </div>
            ))}
          </div>
          {/* Puntuar */}
          <div style={{ padding: `12px ${pad}px 16px`, background: SURFACE, borderTop: isMobile ? `1px solid ${ROW_LINE}` : "none" }}>
            <div style={{ fontSize: 11, fontWeight: 700, color: MUTED, textTransform: "uppercase", letterSpacing: 0.4, marginBottom: 8 }}>{es ? "Puntúa" : "Score"}</div>
            {/* En escritorio, la pregunta y el 1-2-3 en la misma fila: así la
                columna de la derecha no queda mucho más alta que la de preguntas */}
            {cat.items.map(it => (
              <div key={it.id} style={{ marginBottom: isMobile ? 12 : 8, ...(isMobile ? null : { display: "flex", alignItems: "center", gap: 10 }) }}>
                <div style={{ fontSize: 12, color: TEXT, lineHeight: 1.4, ...(isMobile ? { marginBottom: 6 } : { flex: 1, minWidth: 0 }) }}><span style={{ color: FAINT, fontWeight: 700, marginRight: 6 }}>{it.short}</span>{it.label}</div>
                <div style={{ display: "flex", gap: 6, flexShrink: 0 }}>
                  {[1, 2, 3].map(v => {
                    const on = scores[it.id] === v;
                    return (
                      <button key={v} onClick={() => setScores(s => ({ ...s, [it.id]: on ? 0 : v }))}
                        style={{ ...(isMobile ? { flex: 1 } : { width: 40 }), height: isMobile ? 44 : 34, borderRadius: 8, border: `1.5px solid ${on ? BRAND : LINE}`, background: on ? BRAND_LIGHT : WHITE, color: on ? BRAND : TEXT, fontWeight: 700, fontSize: 13, cursor: "pointer", fontFamily: "inherit" }}>{v}</button>
                    );
                  })}
                </div>
              </div>
            ))}
            <div style={{ fontSize: 11, fontWeight: 700, color: MUTED, textTransform: "uppercase", letterSpacing: 0.4, margin: "14px 0 8px" }}>{es ? "Notas" : "Notes"}</div>
            <textarea value={secNotes[cat.id] || ""} onChange={e => setSecNotes(n => ({ ...n, [cat.id]: e.target.value }))} rows={3}
              aria-label={`${es ? "Notas" : "Notes"} · ${cat.name}`}
              placeholder={es ? "Lo que ha contado y lo que te ha llamado la atención…" : "What they told you and what stood out…"}
              style={{ ...field, resize: "vertical", lineHeight: 1.55 }} {...focus} />
          </div>
        </div>
      </div>
    );
  };

  // Lo que hay que aclarar sale de la guía, redactado en su idioma. En las
  // guías anteriores al 23/10 (sin "probe"), las áreas a explorar del informe
  // traducidas al idioma de la entrevista si hace falta (lib/translate.js)
  const weaknessesL = useLocalized(guide?.probe ? [] : candidate?.weaknesses, lang);
  const toProbe = (guide?.probe || weaknessesL).slice(0, 3);
  const opening = [...(guide?.intro || []), ...(guide?.icebreakers || [])];

  return (
    <div role="dialog" aria-modal="true" aria-label={es ? "Modo entrevista" : "Interview mode"}
      style={{ position: "fixed", inset: 0, zIndex: 3000, background: BG, display: "flex", flexDirection: "column", paddingTop: "env(safe-area-inset-top)", boxSizing: "border-box" }}>
      {/* Cabecera fija: quién, cuánto lleva puntuado, guardado y terminar */}
      <div style={{ background: WHITE, borderBottom: `1px solid ${LINE}`, flexShrink: 0 }}>
        <div style={{ maxWidth: 1100, margin: "0 auto", display: "flex", alignItems: "center", gap: 12, padding: isMobile ? "8px 8px 8px 16px" : "12px 24px", flexWrap: "nowrap" }}>
          {avatar}
          <div style={{ flex: 1, minWidth: 0 }}>
            <div style={{ fontSize: 15, fontWeight: 800, color: INK, overflow: "hidden", textOverflow: "ellipsis", whiteSpace: "nowrap" }}>{candidate ? candidate.name : position.title}</div>
            <div style={{ fontSize: 12, color: MUTED, overflow: "hidden", textOverflow: "ellipsis", whiteSpace: "nowrap" }}>
              {candidate ? [candidate.position, candidate.company].filter(Boolean).join(" · ") : (es ? "Entrevista a pantalla completa" : "Full-screen interview")}{started ? ` · ${es ? "entrevistador/a" : "interviewer"}: ${name.trim()}` : ""}
            </div>
          </div>
          {started && !isMobile && (
            <div style={{ textAlign: "right", flexShrink: 0 }}>
              <div style={{ fontSize: 13, fontWeight: 700, color: INK, fontVariantNumeric: "tabular-nums" }}>{scored}/{ids.length} · {pct === null ? "—" : `${Math.round(pct)}%`}</div>
              <div style={{ fontSize: 11, color: typeof saveState === "string" && saveState.length > 12 ? DANGER : MUTED }}>{saveTxt}</div>
            </div>
          )}
          {/* Móvil: el progreso arriba y «Terminar» solo al pie (antes había dos) */}
          {started && isMobile && (
            <div style={{ textAlign: "right", flexShrink: 0, fontSize: 12, color: MUTED, lineHeight: 1.4 }}>
              <div style={{ fontWeight: 700, color: INK, fontVariantNumeric: "tabular-nums" }}>{scored}/{ids.length}{pct === null ? "" : ` · ${Math.round(pct)}%`}</div>
              <div style={{ color: typeof saveState === "string" && saveState.length > 12 ? DANGER : MUTED }}>{saveState === "saving" ? (es ? "Guardando…" : "Saving…") : saveState === "saved" ? "✓" : ""}</div>
            </div>
          )}
          {started && !isMobile && <button onClick={finish} disabled={saveState === "saving"} style={{ ...btn.primary, flexShrink: 0 }}>{es ? "Terminar" : "Finish"}</button>}
          <CloseButton onClick={tryClose} label={es ? "Cerrar" : "Close"} />
        </div>
        {askClose && (
          <div style={{ maxWidth: 1100, margin: "0 auto", padding: isMobile ? "0 16px 12px" : "0 24px 12px" }}>
            <ConfirmBar danger={false} message={es ? "¿Guardar la entrevista antes de salir?" : "Save the interview before leaving?"}
              actionLabel={es ? "Guardar y salir" : "Save and leave"} cancelLabel={es ? "Seguir entrevistando" : "Keep interviewing"}
              onConfirm={finish} onCancel={() => setAskClose(false)} />
          </div>
        )}
      </div>

      <div style={{ flex: 1, minHeight: 0, overflowY: "auto", WebkitOverflowScrolling: "touch" }}>
        {!started ? startCard : (
          <div style={{ maxWidth: 1100, margin: "0 auto", padding: isMobile ? 16 : 24, display: "flex", flexDirection: "column", gap: 16 }}>
            {/* Leyendo la guía guardada; si no la hay, SimpleGuide la prepara
                (con la misma animación) y avisa al guardarla */}
            {!guideLoaded && !hasGuide && <div style={{ ...card, padding: pad }}><GuideLoader lang={lang} /></div>}
            {guideLoaded && !hasGuide && (
              <div style={{ ...card, padding: pad }}>
                <SimpleGuide candidate={candidate} position={position} lang={lang} bare sameLangOnly focusSection={categories[0]?.id || "c1"}
                  onStored={(json) => { const db = parseStored(json); if (db[guideKey]?.sections) setStored(db); }} />
              </div>
            )}

            {/* Para empezar: lo que hay que aclarar y cómo abrir */}
            {(toProbe.length > 0 || opening.length > 0) && (
              <div style={{ ...card, padding: `14px ${pad}px` }}>
                <div style={{ display: "flex", alignItems: "center", justifyContent: "space-between", gap: 10 }}>
                  <span style={text.cardTitle}>{es ? "Para empezar" : "Before you start"}</span>
                  <button onClick={() => setShowOpening(v => !v)} style={{ ...linkBtn, color: MUTED, fontWeight: 600, ...(isMobile ? { minHeight: 40 } : null) }}>{showOpening ? (es ? "Ocultar" : "Hide") : (es ? "Ver" : "Show")}</button>
                </div>
                {showOpening && (
                  <div style={{ display: "grid", gridTemplateColumns: isMobile || !(toProbe.length && opening.length) ? "minmax(0, 1fr)" : "repeat(2, minmax(0, 1fr))", gap: 16, marginTop: 10 }}>
                    {toProbe.length > 0 && (
                      <div>
                        <div style={{ fontSize: 11, fontWeight: 700, color: MUTED, textTransform: "uppercase", letterSpacing: 0.4, marginBottom: 6 }}>{es ? "A explorar (del informe)" : "To probe (from the report)"}</div>
                        {toProbe.map((w, i) => <div key={i} style={{ ...text.body, display: "flex", gap: 6, marginBottom: 4 }}><span style={{ color: FAINT }}>•</span>{w}</div>)}
                      </div>
                    )}
                    {opening.length > 0 && (
                      <div>
                        <div style={{ fontSize: 11, fontWeight: 700, color: MUTED, textTransform: "uppercase", letterSpacing: 0.4, marginBottom: 6 }}>{es ? "Cómo abrir" : "How to open"}</div>
                        {/* Sola, a todo el ancho y en dos columnas: no deja media tarjeta en blanco */}
                        <div style={!isMobile && !toProbe.length ? { columnCount: 2, columnGap: 32 } : undefined}>
                          {opening.map((w, i) => <div key={i} style={{ ...text.body, display: "flex", gap: 6, marginBottom: 4, breakInside: "avoid" }}><span style={{ color: FAINT }}>•</span>{w}</div>)}
                        </div>
                      </div>
                    )}
                  </div>
                )}
              </div>
            )}

            {categories.map(vectorCard)}

            {/* Comentario general y cierre */}
            <div style={{ ...card, padding: pad }}>
              <div style={{ ...text.cardTitle, marginBottom: 8 }}>{es ? "Tu impresión general" : "Your overall impression"}</div>
              <textarea value={general} onChange={e => setGeneral(e.target.value)} rows={4}
                placeholder={es ? "Lo que más te ha convencido, lo que te deja dudas…" : "What convinced you most, what leaves you in doubt…"}
                style={{ ...field, resize: "vertical", lineHeight: 1.55 }} {...focus} />
              {(guide?.closing || []).length > 0 && (
                <div style={{ marginTop: 12 }}>
                  <div style={{ fontSize: 11, fontWeight: 700, color: MUTED, textTransform: "uppercase", letterSpacing: 0.4, marginBottom: 6 }}>{es ? "Para cerrar" : "Closing"}</div>
                  {guide.closing.map((w, i) => <div key={i} style={{ ...text.body, display: "flex", gap: 6, marginBottom: 4 }}><span style={{ color: FAINT }}>•</span>{w}</div>)}
                </div>
              )}
              {isMobile && <div style={{ fontSize: 12, color: MUTED, marginTop: 12 }}>{scored}/{ids.length} {es ? "puntuadas" : "scored"} · {pct === null ? "—" : `${Math.round(pct)}%`} · {saveTxt}</div>}
              {typeof saveState === "string" && saveState.length > 12 && <div style={{ ...errorText, marginTop: 8 }}>{saveState}</div>}
              <button onClick={finish} disabled={saveState === "saving"} style={{ ...btn.primary, width: "100%", marginTop: 14, height: isMobile ? 48 : 42 }}>
                {es ? "Terminar entrevista" : "Finish interview"}
              </button>
            </div>
          </div>
        )}
      </div>
    </div>
  );
}
